'use client';

/**
 * GlassButton - Reusable Glassmorphic Button Component
 *
 * Consistent glass button styling across the application.
 * Companion to GlassCard for interactive elements.
 *
 * Features:
 * - Three variants: default (glass only), primary (glass + gradient), icon (square glass button)
 * - Size variants: sm, md, lg
 * - Disabled state support
 * - Forward ref for proper React integration
 * - asChild support for composition (e.g. with Next.js Link)
 *
 * Usage:
 * ```tsx
 * // Default glass button
 * <GlassButton onClick={handleClick}>Click Me</GlassButton>
 *
 * // Primary CTA with gradient
 * <GlassButton variant="primary" onClick={handleSubmit}>
 *   <Plus className="mr-2 h-4 w-4" />
 *   New Lead
 * </GlassButton>
 *
 * // Small icon button
 * <GlassButton variant="icon" size="sm">
 *   <Settings className="h-4 w-4" />
 * </GlassButton>
 *
 * // As link
 * <GlassButton asChild>
 *   <Link href="/somewhere">Go Somewhere</Link>
 * </GlassButton>
 * ```
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

/**
 * Custom hook to safely handle disabled state during SSR/hydration
 *
 * Problem: When parent components use async loading state (TanStack Query, usePermissions),
 * server renders disabled={true} but client may hydrate with disabled={false} from cache.
 * This causes React hydration mismatch: server has disabled="" but client has no attribute.
 *
 * Solution: During initial render (SSR + first client render), normalize disabled to undefined
 * so both server and client render the same thing (no disabled attribute).
 * After mount, use the actual disabled value.
 *
 * Trade-off: Brief moment where button is clickable before loading state applies,
 * but this is acceptable since it prevents React errors and loading resolves quickly.
 */
function useHydrationSafeDisabled(disabled: boolean | undefined): boolean | undefined {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- canonical SSR hydration mount guard; setMounted(true) in [] effect is the intentional pattern to detect client-side mount and avoid disabled-attribute hydration mismatch
    setMounted(true);
  }, []);

  // During SSR and first client render, always return undefined (no disabled attribute)
  // This ensures server HTML matches client initial render
  // After mount, return the actual disabled state
  if (!mounted) {
    return undefined;
  }

  // After mount: convert false to undefined (no attribute), keep true as true
  return disabled || undefined;
}

// =====================================================
// TYPES
// =====================================================

export interface GlassButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Content to render inside the button
   */
  children?: React.ReactNode;

  /**
   * Visual variant for different use cases
   * - default: Standard glass button
   * - primary: Glass + gradient background (CTAs)
   * - icon: Square glass button (icon-only buttons)
   */
  variant?: 'default' | 'primary' | 'icon';

  /**
   * Size variant
   * - sm: Small button (h-7, text-xs, px-3)
   * - md: Medium button (h-9, text-sm, px-4) - default
   * - lg: Large button (h-11, text-base, px-6)
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether to render as a child component (delegation)
   */
  asChild?: boolean;
}

// =====================================================
// COMPONENT
// =====================================================

/**
 * GlassButton Component
 *
 * Provides consistent glassmorphic button styling across the application.
 * Uses the same glass formula as GlassCard for visual consistency.
 *
 * Wrapped with React.memo for performance optimization.
 */
export const GlassButton = React.memo(
  // eslint-disable-next-line @eslint-react/no-forward-ref -- flagged: forwardRef removal requires React 19 ref-as-prop migration across all consumers; coordinate with design-tier upgrade
  React.forwardRef<HTMLButtonElement, GlassButtonProps>(
    (
      {
        variant = 'default',
        size = 'md',
        asChild = false,
        className,
        children,
        disabled,
        ...props
      },
      ref
    ) => {
      const Comp = asChild ? Slot : 'button';
      // 🔒 HYDRATION FIX: Use hydration-safe disabled state to prevent SSR/client mismatch
      // See useHydrationSafeDisabled docs above for explanation
      const safeDisabled = useHydrationSafeDisabled(disabled);
      return (
        <Comp
          ref={ref}
          disabled={safeDisabled}
          className={cn(
            // Base glass effect from glass-interactive class
            'glass-interactive',
            'rounded-lg',
            'font-medium',
            'inline-flex items-center justify-center',

            // Hover effects
            'hover:border-brand-primary/60 dark:hover:border-brand-primary/40',
            'hover:scale-[1.01]',
            'hover:shadow-lg dark:hover:shadow-md',

            // Disabled state
            'disabled:opacity-50',
            'disabled:cursor-not-allowed',
            'disabled:hover:scale-100',

            // Size variants
            size === 'sm' && 'h-7 px-3 py-1.5 text-xs',
            size === 'md' && 'h-9 px-4 py-2 text-sm',
            size === 'lg' && 'h-11 px-6 py-3 text-base',

            // Variant: default (glass background)
            variant === 'default' && ['text-gray-900 dark:text-white'],

            // Variant: primary (glass + gradient background for CTAs)
            variant === 'primary' && [
              'bg-gradient-to-r from-brand-primary to-brand-accent',
              'text-black',
              'font-semibold',
              'hover:shadow-xl hover:shadow-brand-accent/30',
              'border-brand-accent/40',
            ],

            // Variant: icon (square button for icons)
            variant === 'icon' && [
              'text-gray-900 dark:text-white',
              size === 'sm' && 'w-7 h-7 p-0',
              size === 'md' && 'w-9 h-9 p-0',
              size === 'lg' && 'w-11 h-11 p-0',
            ],

            className
          )}
          {...props}
        >
          {children}
        </Comp>
      );
    }
  )
);

GlassButton.displayName = 'GlassButton';
